import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Plus } from 'lucide-react';
import InvoiceForm from '../components/invoices/InvoiceForm';
import InvoiceList from '../components/invoices/InvoiceList';
import InvoiceFilters from '../components/invoices/InvoiceFilters';
import InvoiceSummary from '../components/invoices/InvoiceSummary';
import Pagination from '../components/common/Pagination';
import { useInvoices } from '../hooks/useInvoices';
import { useProjects } from '../hooks/useProjects';
import { useEffectiveCompanyId } from '../hooks/useEffectiveCompany';
import type { Invoice, InvoiceFilter, InvoicePaginationParams } from '../types';

const Invoices: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | undefined>(undefined);
  
  // Estado de filtros
  const [filters, setFilters] = useState<InvoiceFilter>({});
  
  // Estado de paginación
  const [pagination, setPagination] = useState<InvoicePaginationParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'invoice_date',
    sortOrder: 'desc',
  });
  
  // Cargar datos
  const { data: invoiceData, isLoading } = useInvoices(filters, pagination);
  const { data: projects = [] } = useProjects();
  const companyId = useEffectiveCompanyId();
  
  const handleCreateNew = () => {
    setEditingInvoice(undefined);
    setShowForm(true);
  };
  
  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setShowForm(true);
  };
  
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingInvoice(undefined);
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingInvoice(undefined);
  };
  
  const handleFilterChange = (newFilters: InvoiceFilter) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 }); // Reset to first page
  };
  
  const handleResetFilters = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
  };
  
  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Facturas Emitidas</h1>
        {!showForm && (
          <Button onClick={handleCreateNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Nueva Factura
          </Button>
        )}
      </div>
      
      {showForm ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {editingInvoice ? 'Editar Factura' : 'Nueva Factura'}
          </h2>
          <InvoiceForm
            invoice={editingInvoice}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      ) : (
        <>
          {/* Filtros */}
          <InvoiceFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            projects={projects.map(p => ({ projectId: p.projectId, name: p.name }))}
            companyId={companyId}
          />
          
          {/* Resumen de estadísticas */}
          {invoiceData && invoiceData.items.length > 0 && (
            <InvoiceSummary invoices={invoiceData.items} />
          )}
          
          {/* Lista de facturas */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : invoiceData && invoiceData.items.length > 0 ? (
              <>
                <InvoiceList
                  invoices={invoiceData.items}
                  isLoading={isLoading}
                  onEdit={handleEdit}
                  companyId={companyId}
                />
                
                {/* Paginación */}
                <Pagination
                  currentPage={invoiceData.pagination.page}
                  totalPages={invoiceData.pagination.totalPages}
                  onPageChange={handlePageChange}
                  pageSize={invoiceData.pagination.pageSize}
                  totalItems={invoiceData.pagination.total}
                />
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No hay facturas que coincidan con los filtros
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Invoices;
